import React, { useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, UserCog, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import CreateAdvisorDialog from '@/components/admin/CreateAdvisorDialog';

/**
 * Iter 41.35 — Advisor management page (hm_admin only).
 *
 * Lists every `hm_advisor` user with the size of their client book + status.
 * Each row navigates to /admin/advisors/:id for the detail view.
 */
export default function AdminAdvisors() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [q, setQ] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      setRows(await adminApi.listAdvisors());
    } catch (e) {
      toast.error(t('pages.advisors.loadFailed', { defaultValue: 'Failed to load advisors' }), {
        description: formatApiError(e),
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(needle) ||
      (r.email || '').toLowerCase().includes(needle)
    );
  }, [rows, q]);

  return (
    <>
      <Topbar
        title={t('pages.advisors.title', { defaultValue: 'Advisors' })}
        subtitle={t('pages.advisors.subtitle', { defaultValue: 'Manage CTH advisors and their assigned client books.' })}
      />
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-advisors-page">
        {/* Toolbar */}
        <Card className="p-4 shadow-card flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('pages.advisors.searchPh', { defaultValue: 'Search by name or email…' })}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-9"
              data-testid="advisors-search"
            />
          </div>
          <Button onClick={() => setCreateOpen(true)}
                  className="bg-gradient-primary hover:opacity-90"
                  data-testid="advisors-create-btn">
            <Plus className="h-4 w-4 mr-1.5" />
            {t('pages.advisors.create', { defaultValue: 'Create advisor' })}
          </Button>
        </Card>

        {/* Table */}
        <Card className="shadow-card overflow-hidden" data-testid="advisors-table-card">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 flex flex-col items-center text-muted-foreground" data-testid="advisors-empty">
              <UserCog className="h-8 w-8 mb-2" />
              <p className="text-sm">
                {q
                  ? t('pages.advisors.emptySearch', { defaultValue: 'No advisor matches your search.' })
                  : t('pages.advisors.empty', { defaultValue: 'No advisors yet. Create the first one.' })}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pages.advisors.col.name',      { defaultValue: 'Name' })}</TableHead>
                  <TableHead>{t('pages.advisors.col.email',     { defaultValue: 'Email' })}</TableHead>
                  <TableHead className="text-right">{t('pages.advisors.col.clients',   { defaultValue: 'Clients' })}</TableHead>
                  <TableHead>{t('pages.advisors.col.createdAt', { defaultValue: 'Created' })}</TableHead>
                  <TableHead>{t('pages.advisors.col.status',    { defaultValue: 'Status' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow key={a.id}
                            className="cursor-pointer hover:bg-muted/40"
                            onClick={() => navigate(`/admin/advisors/${a.id}`)}
                            data-testid={`advisor-row-${a.id}`}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-muted-foreground">{a.email}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.client_count}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString(getUiLocale()) : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={a.status === 'active' ? 'default' : 'secondary'}
                             className={a.status === 'active' ? 'bg-success/15 text-success border-success/30' : ''}>
                        {a.status === 'active'
                          ? t('pages.advisors.status.active',   { defaultValue: 'Active' })
                          : t('pages.advisors.status.inactive', { defaultValue: 'Inactive' })}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <CreateAdvisorDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={reload}
      />
    </>
  );
}
